import React, { ComponentProps, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { PublicPageLayout } from '@adsk/alloy-project-page-layout';
import { LinkButton } from '@adsk/alloy-react-button';
import EmptyState from '@adsk/alloy-react-empty-state';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

export interface DefaultActionProps {
  onRetry?: () => void;
}

export const DefaultAction = ({
  onRetry = () => window.location.reload(),
}: DefaultActionProps) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  return (
    <LinkButton onClick={() => onRetry?.()}>
      {T.translate('TRY_AGAIN')}
    </LinkButton>
  );
};

type EmptyStateProps = ComponentProps<typeof EmptyState>;

type ErrorPageProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  illustrationType?: EmptyStateProps['illustrationType'];
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  onLogout?: () => void;
};

/**
 * An ErrorPage component. Used to show a generic error page when no other option exists.
 * Can be used as a fallback when using a page level ErrorBoundary for example.
 */
const ErrorPage = React.forwardRef<HTMLDivElement, ErrorPageProps>(
  (
    {
      illustrationType = EmptyState.ILLUSTRATION_TYPES
        .BUILDING_CONSTRUCTION_GREY,
      title,
      description,
      action,
      footer,
      onLogout,
      style,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const finalTitle = title || T.translate('ERROR_PAGE_TITLE');
    const finalDescription =
      description || T.translate('ERROR_PAGE_DESCRIPTION');
    const finalAction = action || <DefaultAction />;

    return (
      <PublicPageLayout onLogout={onLogout} ref={ref}>
        <div
          css={[
            {
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              height: '100%',
              justifyContent: 'center',
            },
            style,
          ]}
          {...props}
        >
          <EmptyState
            title={finalTitle}
            illustrationType={illustrationType}
            description={finalDescription}
            size={EmptyState.SIZES.LARGE}
          >
            <>
              {finalAction && (
                <div css={[theme.typography.heading2]}>{finalAction}</div>
              )}
              {footer && (
                <div
                  css={[
                    {
                      marginTop: 72,
                      color: theme.colors.charcoal700,
                      ...theme.typography.bodyLargeBold,
                    },
                  ]}
                >
                  {footer}
                </div>
              )}
            </>
          </EmptyState>
        </div>
      </PublicPageLayout>
    );
  },
);

ErrorPage.displayName = 'ErrorPage';

ErrorPage.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Illustration type */
  illustrationType: PropTypes.oneOf(
    Object.values(EmptyState.ILLUSTRATION_TYPES),
  ),
  /** Error page title */
  title: PropTypes.node,
  /** Error page description */
  description: PropTypes.node,
  /** Error page action */
  action: PropTypes.node,
  /** Error page footer */
  footer: PropTypes.node,
  /** callback called on logout */
  onLogout: PropTypes.func,
};

export default Object.assign(ErrorPage, {
  ILLUSTRATION_TYPES: EmptyState.ILLUSTRATION_TYPES,
});
